import React from "react";
import { motion } from "framer-motion";
import { Phone, MapPin } from "lucide-react";

const WANSPORT_URL = "https://wansport.com";

export const Navbar = () => {
  const [scrolled, setScrolled] = React.useState(false);

  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { label: "Home", href: "#home" },
    { label: "Servizi", href: "#servizi" },
    { label: "Bar", href: "#bar" },
    { label: "Tornei", href: "#tornei" },
    { label: "WhatsApp", href: "#whatsapp" },
    { label: "Contatti", href: "#contatti" },
  ];

  return (
    <motion.header
      initial={{ y: -30, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.2, 0.8, 0.2, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-2xl bg-[#0A0A0A]/75 border-b border-white/10"
          : "bg-transparent"
      }`}
      data-testid="main-navbar"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 h-20 flex items-center justify-between">
        <a href="#home" className="flex items-center gap-3" data-testid="navbar-logo">
          <img
            src="https://customer-assets.emergentagent.com/job_elvo-tennis-padel/artifacts/6mal192o_elvopadel.jpg"
            alt="Padel Elvo"
            className="w-11 h-11 rounded-full object-cover bg-white"
          />
          <div className="leading-tight">
            <div className="font-oswald uppercase tracking-[0.2em] text-sm text-white">Padel</div>
            <div className="font-oswald uppercase tracking-[0.2em] text-sm text-[#CCFF00] -mt-1">Elvo</div>
          </div>
        </a>

        <nav className="hidden md:flex items-center gap-10">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="font-oswald uppercase tracking-[0.15em] text-sm text-white/80 hover:text-white link-underline"
              data-testid={`nav-link-${l.label.toLowerCase()}`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <a
          href={WANSPORT_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-primary px-5 py-3 text-sm inline-flex items-center gap-2"
          data-testid="navbar-prenota-button"
        >
          Prenota
          <span aria-hidden>→</span>
        </a>
      </div>
    </motion.header>
  );
};

export const Hero = ({ image }) => {
  return (
    <section id="home" className="relative min-h-[100vh] overflow-hidden" data-testid="hero-section">
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${image})` }}
      />
      <div className="absolute inset-0 hero-gradient" />
      <div className="absolute inset-0 bg-[#0A0A0A]/40" />
      <div className="absolute inset-0 court-lines opacity-50" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 pt-40 md:pt-48 pb-24">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="flex items-center gap-3 mb-8"
        >
          <span className="h-px w-10 bg-[#CCFF00]" />
          <span className="font-oswald uppercase tracking-[0.3em] text-xs text-[#CCFF00]">
            Padel Club · Occhieppo Superiore
          </span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.35 }}
          className="font-oswald font-bold uppercase tracking-tighter text-white leading-[0.9] text-5xl sm:text-7xl lg:text-[8.5rem]"
          data-testid="hero-title"
        >
          Gioca.<br />
          Sfida. <span className="text-[#CCFF00]">Vinci.</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.55 }}
          className="mt-8 max-w-xl text-lg text-white/70 leading-relaxed"
        >
          Tre campi indoor di ultima generazione, Ghost Padel e maestri qualificati
          a Occhieppo Superiore. 15€ a persona per 1h 30min. Scendi in campo.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.7 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            href={WANSPORT_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary px-8 py-4 text-base inline-flex items-center gap-3"
            data-testid="hero-prenota-button"
          >
            Prenota su Wansport
            <span aria-hidden>→</span>
          </a>
          <a
            href="#servizi"
            className="btn-ghost px-8 py-4 text-base inline-flex items-center gap-3"
            data-testid="hero-scopri-button"
          >
            Scopri il Club
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1 }}
          className="absolute bottom-8 left-6 lg:left-8 right-6 lg:right-8 flex flex-wrap items-end justify-between gap-8"
        >
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2 text-white/80">
              <Phone size={16} className="text-[#CCFF00]" />
              <a href="tel:+393336043477" className="font-dm text-sm link-underline" data-testid="hero-phone-link">
                333 604 3477
              </a>
            </div>
            <div className="flex items-center gap-2 text-white/80">
              <MapPin size={16} className="text-[#CCFF00]" />
              <span className="font-dm text-sm">Occhieppo Superiore, BI</span>
            </div>
          </div>
          <div className="hidden lg:block text-right">
            <div className="font-oswald text-5xl text-white leading-none">15€</div>
            <div className="font-oswald uppercase tracking-[0.2em] text-[10px] text-[#CCFF00] mt-2">
              A Persona · 1h 30min
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
